import { createApi } from '@reduxjs/toolkit/query/react';
import { toast } from 'react-toastify';
import { ErrorMessageDataType } from 'shared/types';
import { baseQueryWithReauth } from '../../shared/store';
import {
  IGetAdminAwards,
  IGetAdminCompetition,
  IGetAdminCompetitionStats,
  IGetCompetitions,
  MGetAdminCompetitionItemAwards,
  MGetAdminCompetitionItemParticipant,
  MGetAdminCompetitionItemParticipants,
  MGetAdminCompetitionItemRatingHistory,
  MGetAdminCompetitionItemReport,
  MGetAdminCompetitionPrizePlace,
  MGetAdminCompetitionPrizePlaces,
  MGetAdminCompetitionsReport,
  MGetCompetitionSalePoint,
  MPatchDeliveryInfo,
  MRejectAward,
  MSendAward,
} from './types';

const competitionsB2BApi = createApi({
  reducerPath: 'competitionsB2BApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'allCompetitionsB2B',
    'competitionAwardPlaces',
    'competition',
    'competitionStats',
    'competitionAwards',
    'competitionParticipants',
    'competitionRatingHistory',
    'competitionRatingPrizePlaces',
    'competitionRatingPrizePlace',
    'competitionSalePoint',
  ],
  endpoints: (builder) => ({
    getCompetitions: builder.query<IGetCompetitions.Response, IGetCompetitions.Params>({
      query: ({ page, size, search }) =>
        `/competitions/admin/competition/?app_type=b2b&page=${page}&size=${size}&search=${search}`,
      providesTags: ['allCompetitionsB2B'],
    }),
    getAdminAwards: builder.query<IGetAdminAwards.Response, IGetAdminAwards.Params>({
      query: ({ competition_id }) => `/competitions/admin/competition/${competition_id}/award`,
      providesTags: (result, error) => {
        const errorMessage = 'Возникла ошибка при получении призовых мест';
        if (error) {
          if (error.status === 500) {
            toast.error(errorMessage.toString(), {
              position: toast.POSITION.TOP_RIGHT,
            });
          } else {
            toast.error((error.data as ErrorMessageDataType).detail, {
              position: toast.POSITION.TOP_RIGHT,
            });
          }
        }
        return ['competitionAwardPlaces'];
      },
    }),
    getAdminCompetitionItem: builder.query<IGetAdminCompetition.Response, IGetAdminCompetition.Params>({
      query: ({ competition_id }) => `/competitions/admin/competition/${competition_id}`,
      providesTags: ['competition'],
    }),
    getAdminCompetitionItemStats: builder.query<IGetAdminCompetitionStats.Response, IGetAdminCompetitionStats.Params>({
      query: ({ competition_id }) => `/competitions/admin/competition/${competition_id}/participant/purchase_amount`,
      providesTags: ['competitionStats'],
    }),
    getAdminCompetitionItemAwardPlaces: builder.query<
      MGetAdminCompetitionItemAwards.Response,
      MGetAdminCompetitionItemAwards.Params
    >({
      query: () => '/competitions/admin/competition/award_places',
      providesTags: ['competitionAwards'],
    }),
    getAdminCompetitionItemParticipants: builder.query<
      MGetAdminCompetitionItemParticipants.Response | void,
      MGetAdminCompetitionItemParticipants.Params
    >({
      query: ({ competition_id }) => `/competitions/admin/competition/${competition_id}/participant`,
      providesTags: ['competitionParticipants'],
    }),
    getAdminCompetitionItemParticipant: builder.query<
      MGetAdminCompetitionItemParticipant.Response | void,
      MGetAdminCompetitionItemParticipant.Params
    >({
      query: ({ competition_id, participant_id }) =>
        `/competitions/admin/competition/${competition_id}/participant/${participant_id}`,
      providesTags: ['competitionParticipants'],
    }),
    getAdminCompetitionItemReport: builder.mutation<
      MGetAdminCompetitionItemReport.Response,
      MGetAdminCompetitionItemReport.Params
    >({
      query: ({ competition_id }) => ({
        url: `/competitions/admin/competition/${competition_id}/report`,
        responseHandler: async (r) => r.blob(),
      }),
    }),
    getAdminCompetitionsReport: builder.mutation<
      MGetAdminCompetitionsReport.Response,
      MGetAdminCompetitionsReport.Params | void
    >({
      query: () => ({
        url: '/competitions/admin/competition/report//by_participant',
        responseHandler: async (r) => r.blob(),
      }),
    }),
    getAdminCompetitionItemRatingHistory: builder.query<
      MGetAdminCompetitionItemRatingHistory.Response,
      MGetAdminCompetitionItemRatingHistory.Params
    >({
      query: ({ competitionId, participantId }) =>
        `/competitions/admin/competition/${competitionId}/participant/${participantId}/rating-history`,
      providesTags: ['competitionRatingHistory'],
    }),
    getAdminCompetitionItemPrizePlace: builder.query<
      MGetAdminCompetitionPrizePlace.Response,
      MGetAdminCompetitionPrizePlace.Params
    >({
      query: ({ award_place_id }) => ({
        url: `/competitions/admin/competition/award_places/${award_place_id}`,
      }),
      providesTags: ['competitionRatingPrizePlace'],
    }),
    getAdminCompetitionItemPrizePlaces: builder.query<
      MGetAdminCompetitionPrizePlaces.Response,
      MGetAdminCompetitionPrizePlaces.Params | void
    >({
      query: (params) => ({
        url: '/competitions/admin/competition/award_places/all/',
        params: params || undefined,
      }),
      providesTags: ['competitionRatingPrizePlaces'],
    }),
    patchAwardDeliveryInfo: builder.mutation<MPatchDeliveryInfo.Response, MPatchDeliveryInfo.Params>({
      query: (body) => ({
        url: `/competitions/admin/competition/award_places/${body.award_place_id}/delivery-info/`,
        body: {
          ...body,
          award_place_id: undefined,
          id: undefined,
          created_at: undefined,
        },
        method: 'PATCH',
      }),
      invalidatesTags: ['competition'],
    }),
    rejectAward: builder.mutation<MRejectAward.Response, MRejectAward.Params>({
      query: (body) => ({
        url: `/competitions/admin/competition/award_places/${body.awardPlaceId}/reject/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['competition'],
    }),
    sendAward: builder.mutation<MSendAward.Response, MSendAward.Params>({
      query: (body) => ({
        url: `/competitions/admin/competition/award_places/${body.awardPlaceId}/award-send/`,
        method: 'POST',
      }),
      invalidatesTags: ['competition'],
    }),
    addCoins: builder.mutation<MSendAward.Response, MSendAward.Params>({
      query: (body) => ({
        url: `/competitions/admin/competition/award_places/${body.awardPlaceId}/add-coins`,
        method: 'POST',
      }),
      invalidatesTags: ['competition'],
    }),
    getCompetitionSalePoint: builder.query<MGetCompetitionSalePoint.Response, MGetCompetitionSalePoint.Params>({
      query: (params) => ({
        url: `/competitions/admin/competition/${params.competition_id}/participant-my/`,
        params,
      }),
      providesTags: ['competitionSalePoint'],
    }),
  }),
});

export { competitionsB2BApi };
export const {
  useGetCompetitionsQuery,
  useGetAdminAwardsQuery,
  useGetAdminCompetitionItemQuery,
  useGetAdminCompetitionItemStatsQuery,
  useGetAdminCompetitionItemAwardPlacesQuery,
  useGetAdminCompetitionItemParticipantsQuery,
  useGetAdminCompetitionItemParticipantQuery,
  useGetAdminCompetitionItemReportMutation,
  useGetAdminCompetitionItemRatingHistoryQuery,
  useGetAdminCompetitionItemPrizePlacesQuery,
  useGetAdminCompetitionItemPrizePlaceQuery,
  useGetAdminCompetitionsReportMutation,
  usePatchAwardDeliveryInfoMutation,
  useRejectAwardMutation,
  useSendAwardMutation,
  useAddCoinsMutation,
  useGetCompetitionSalePointQuery,
} = competitionsB2BApi;
